import { useNavigate } from 'react-router-dom'
import type { DesignSummary, DesignStatus } from '@/types/design'
import type { Lang } from '@/hooks/useLang'

interface DesignCardProps {
  design: DesignSummary
  lang: Lang
}

const STATUS_META: Record<DesignStatus, { fr: string; en: string; color: string; icon: string }> = {
  draft: { fr: 'Brouillon', en: 'Draft', color: 'text-on-surface-variant bg-surface-container', icon: 'edit_note' },
  completed: { fr: 'Finalisé', en: 'Completed', color: 'text-primary-container bg-primary-container/10', icon: 'check_circle' },
  exported: { fr: 'Exporté', en: 'Exported', color: 'text-secondary bg-secondary/10', icon: 'picture_as_pdf' },
}

const ROLE_ICONS: Record<string, string> = {
  accueil: 'hail',
  assistance_personnelle: 'smart_toy',
  securite: 'security',
  retail: 'shopping_bag',
  aide_domicile: 'home_health',
  evenementiel: 'celebration',
  industrie_legere: 'precision_manufacturing',
  sur_mesure: 'architecture',
}

const ROLE_LABELS: Record<string, { fr: string; en: string }> = {
  accueil: { fr: 'Accueil', en: 'Reception' },
  assistance_personnelle: { fr: 'Assistance pers.', en: 'Personal assist.' },
  securite: { fr: 'Sécurité', en: 'Security' },
  retail: { fr: 'Retail', en: 'Retail' },
  aide_domicile: { fr: 'Aide domicile', en: 'Home care' },
  evenementiel: { fr: 'Événementiel', en: 'Events' },
  industrie_legere: { fr: 'Industrie légère', en: 'Light industry' },
  sur_mesure: { fr: 'Sur mesure', en: 'Custom' },
}

const AI_LABELS: Record<number, { fr: string; en: string }> = {
  1: { fr: 'Téléopéré', en: 'Teleoperated' },
  2: { fr: 'Assisté', en: 'Assisted' },
  3: { fr: 'Supervisé', en: 'Supervised' },
  4: { fr: 'Autonome', en: 'Autonomous' },
  5: { fr: 'Plein IA', en: 'Full AI' },
}

function formatDate(iso: string, lang: Lang): string {
  return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export default function DesignCard({ design, lang }: DesignCardProps) {
  const navigate = useNavigate()
  const status = STATUS_META[design.status]
  const roleIcon = ROLE_ICONS[design.role] ?? 'smart_toy'
  const roleLabel = ROLE_LABELS[design.role]?.[lang] ?? design.role

  return (
    <article
      onClick={() => void navigate(`/designs/${design.id}`)}
      className="glass-card rounded-xl border border-outline-variant/20 p-6 flex flex-col gap-5 cursor-pointer group"
    >
      {/* Top row: icon + status badge */}
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl bg-surface-container-highest flex items-center justify-center text-primary group-hover:text-primary-container transition-colors">
          <span className="material-symbols-outlined text-2xl">{roleIcon}</span>
        </div>
        <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-label tracking-widest uppercase ${status.color}`}>
          <span className="material-symbols-outlined text-xs">{status.icon}</span>
          {status[lang]}
        </span>
      </div>

      {/* Name + role */}
      <div>
        <h3 className="font-headline text-lg font-bold text-on-surface group-hover:text-primary transition-colors leading-tight mb-1">
          {design.name}
        </h3>
        <span className="label-technical">{roleLabel}</span>
      </div>

      {/* Scores mini-bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs font-label text-on-surface-variant">
          <span>{lang === 'fr' ? 'Faisabilité' : 'Feasibility'}</span>
          <span className="text-primary-container font-bold">{design.scores.feasibility}%</span>
        </div>
        <div className="h-1 w-full bg-surface-container-highest rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-container to-secondary rounded-full transition-all duration-700"
            style={{ width: `${design.scores.feasibility}%` }}
          />
        </div>
      </div>

      {/* Meta row */}
      <div className="flex items-center justify-between pt-2 border-t border-outline-variant/10">
        <div className="flex items-center gap-3 text-xs text-on-surface-variant">
          <span className="flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">psychology</span>
            {AI_LABELS[design.ai_level]?.[lang] ?? `L${design.ai_level}`}
          </span>
          <span className="flex items-center gap-1">
            <span className="material-symbols-outlined text-sm">task_alt</span>
            {design.missions_count}
          </span>
        </div>
        <span className="text-[10px] text-on-surface-variant/50 font-label">
          {formatDate(design.updated_at, lang)}
        </span>
      </div>

      {/* Budget chip */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-label text-on-surface-variant">{design.budget_range}</span>
        <span className="material-symbols-outlined text-sm text-primary/40 group-hover:text-primary transition-colors">arrow_forward</span>
      </div>
    </article>
  )
}
